import { motion } from 'motion/react'
import { LuBookOpen, LuHeadphones, LuShield, LuGlobe } from 'react-icons/lu'
import { Link } from '@tanstack/react-router'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { WavyDivider, StarTileRect } from './ContactSvgs'

// ─── Data ─────────────────────────────────────────────────────────────────────

const COMMITMENTS = [
  {
    icon: <LuHeadphones />,
    title: '24-Hour Response',
    description:
      'Our dedicated support team responds to every inquiry within 24 hours, in sha Allah.',
  },
  {
    icon: <LuBookOpen />,
    title: 'Expert Guidance',
    description:
      'Connect directly with qualified scholars and instructors for your Islamic learning journey.',
  },
  {
    icon: <LuShield />,
    title: 'Privacy Protected',
    description:
      'Your personal information is handled with the utmost care and Islamic principles of trust.',
  },
  {
    icon: <LuGlobe />,
    title: 'Global Community',
    description:
      'We serve students from over 40 countries. Wherever you are, we are here for you.',
  },
]

// ─── Card ─────────────────────────────────────────────────────────────────────

type CommitmentCardProps = (typeof COMMITMENTS)[number]

function CommitmentCard({ icon, title, description }: CommitmentCardProps) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="group flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:border-primary-200 hover:shadow-[0_12px_40px_rgba(255,182,6,0.1)]"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 text-xl transition-all duration-300 group-hover:bg-primary-500 group-hover:text-neutral-900">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-neutral-900">{title}</h3>
      <p className="text-sm leading-relaxed text-neutral-500">{description}</p>
    </motion.div>
  )
}

// ─── CTA Banner ───────────────────────────────────────────────────────────────

function CtaBanner() {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-primary-900 px-8 py-12 text-center">
      {/* Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(55% 60% at 50% 0%, rgba(255,182,6,0.2) 0%, transparent 70%)',
        }}
      />
      {/* Star tile */}
      <StarTileRect
        patternId="cta-stars"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.04]"
      />

      <div className="relative z-10 flex flex-col items-center gap-5">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-400">
          Bismillah — Begin Your Journey
        </p>
        <h3 className="max-w-2xl text-3xl font-bold text-white sm:text-4xl">
          Ready to Start Learning the Quran?
        </h3>
        <p className="max-w-xl text-base leading-relaxed text-neutral-300">
          Take the first step towards connecting with the Word of Allah. Our
          expert teachers are waiting to guide you.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a
            href="#contact-form"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-bold text-neutral-900 transition-all duration-300 hover:bg-primary-400 hover:shadow-[0_8px_30px_rgba(255,182,6,0.35)]"
          >
            Contact Us Now
          </a>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/10 hover:border-white/30"
          >
            Browse Courses
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Section ──────────────────────────────────────────────────────────────────

export default function ContactCommitmentsSection() {
  return (
    <section
      id="contact-commitments"
      className="relative w-full overflow-hidden bg-primary-50 py-20 sm:py-28"
    >
      {/* Wavy top border */}
      <div className="absolute top-0 left-0 w-full px-8">
        <WavyDivider className="w-full opacity-40" />
      </div>

      {/* Right-side star watermark */}
      <StarTileRect
        patternId="commitments-stars"
        className="pointer-events-none absolute right-0 top-0 h-full w-1/3 opacity-[0.025]"
      />

      <div className="container relative z-10 mx-auto flex flex-col gap-14">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center gap-3 text-center">
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-700">
              Our Promise
            </p>
          </RevealItem>
          <RevealItem>
            <h2 className="text-4xl font-bold text-neutral-900 sm:text-5xl">
              Why Families{' '}
              <span className="relative inline-block">
                <span className="relative z-10">Trust Us</span>
                <span
                  aria-hidden="true"
                  className="absolute bottom-1 left-0 z-0 h-3 w-full rounded-sm opacity-40"
                  style={{
                    background:
                      'linear-gradient(90deg, #ffb606 0%, #e8a605 100%)',
                  }}
                />
              </span>
            </h2>
          </RevealItem>
          <RevealItem className="max-w-xl">
            <p className="text-base leading-relaxed text-neutral-500">
              We're committed to a warm, responsive, and respectful experience
              for every student and family who reaches out.
            </p>
          </RevealItem>
        </ScrollReveal>

        {/* Cards */}
        <ScrollReveal
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          stagger={0.1}
        >
          {COMMITMENTS.map((item) => (
            <RevealItem key={item.title}>
              <CommitmentCard {...item} />
            </RevealItem>
          ))}
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal>
          <RevealItem>
            <CtaBanner />
          </RevealItem>
        </ScrollReveal>
      </div>
    </section>
  )
}
